import { createAsyncThunk, createSlice, SerializedError, Draft } from '@reduxjs/toolkit';
import { AlertId, AlgoliaAlert } from '../../../types/alerts';
import { getAlgoliaAlert } from '../../../utils/alertsHelper';
import { GroupSearchParams, getGroupFilters } from '../../../utils/algoliaHelper';
import { search } from '../../../utils/requests/algoliaRequests';
import { TimedRecord, timedRecordGet, timedRecordSet } from '../../../utils/timedRecord';
import { DEFAULT_CACHE_EXPIRY_MS } from '../../config';
import { createAlert, updateAlert, deleteAlert } from '../../thunks/alerts';
import { fetchTrails } from '../../thunks/filters';

export const ALERT_CACHE_EXPIRY_MS = DEFAULT_CACHE_EXPIRY_MS;

type AlertMap = Record<AlertId, AlgoliaAlert>;

type FetchArgs = GroupSearchParams & {
  draft?: boolean;
};

export type AlertsState = {
  draftAlerts: AlertMap;
  alerts: AlertMap;
  started: boolean;
  loading: boolean;
  error: SerializedError;
  alertCache: TimedRecord<AlertId, AlgoliaAlert>;
};

export const initialState: AlertsState = {
  draftAlerts: {},
  alerts: {},
  started: false,
  loading: false,
  error: null,
  alertCache: {}
};

export const selectStatus = ({ alerts: { started, loading, alerts } }: { alerts: AlertsState }) => ({ started, loading, alerts });

export const fetchGroupsAlerts = createAsyncThunk('alerts/fetchGroupsAlerts', async ({ indexId, groups, filters }: FetchArgs) => {
  // Prevent searching without any group_ids filter
  if (groups.length === 0) {
    return [];
  }

  const objectTypeFilter = 'object_type:area';
  const statusFilter = 'NOT status:D';
  const groupFilters = getGroupFilters(groups);
  const otherFilters = `${objectTypeFilter} AND ${statusFilter} AND ${groupFilters}`;
  const attributesToRetrieve = ['ID', 'object_type', 'object_id', 'name', 'alert_type', 'status', 'start_date', 'end_date'];

  if (filters) {
    filters = `${filters} AND ${otherFilters}`;
  } else {
    filters = otherFilters;
  }

  const response = await search<AlgoliaAlert>(indexId, '', { attributesToRetrieve, filters, hitsPerPage: 1000 });
  return response.hits;
});

const addAlertsFromCache = (state: Draft<AlertsState>, alerts: AlertMap) => {
  Object.keys(state.alertCache).forEach(alertIdString => {
    const alertId = parseInt(alertIdString);
    const alert = timedRecordGet(state.alertCache, alertId, ALERT_CACHE_EXPIRY_MS);
    // Note that this overwrites the alert if it was already present in search results
    if (alert) {
      alerts[alertId] = alert;
    }
  });
};

export const alertsSlice = createSlice({
  name: 'alerts',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchTrails.pending, (state, action) => {
        state.draftAlerts = {};

        if (!action.meta.arg.draft) {
          state.started = true;
          state.loading = false;
          state.alerts = {};
          state.error = null;
        }
      })
      .addCase(fetchGroupsAlerts.pending, (state, action) => {
        state.draftAlerts = {};

        if (!action.meta.arg.draft) {
          state.started = true;
          state.loading = true;
          state.alerts = {};
          state.error = null;
        }
      })
      .addCase(fetchGroupsAlerts.fulfilled, (state, action) => {
        const alerts: AlertMap = {};
        action.payload.forEach(alert => {
          alerts[alert.ID] = alert;
        });
        addAlertsFromCache(state, alerts);

        if (action.meta.arg.draft) {
          state.draftAlerts = alerts;
        } else {
          state.loading = false;
          state.alerts = alerts;
        }
      })
      .addCase(fetchGroupsAlerts.rejected, (state, action) => {
        if (action.meta.arg.draft) {
          return;
        }

        state.loading = false;
        state.error = action.error;
      })
      .addCase(createAlert.fulfilled, (state, action) => {
        action.payload.forEach(alert => {
          state.alerts[alert.id] = getAlgoliaAlert(alert);
          timedRecordSet(state.alertCache, alert.id, getAlgoliaAlert(alert));
        });
      })
      .addCase(updateAlert.fulfilled, (state, action) => {
        const alert = action.payload;
        state.alerts[alert.id] = getAlgoliaAlert(alert);
        timedRecordSet(state.alertCache, alert.id, getAlgoliaAlert(alert));
      })
      .addCase(deleteAlert.fulfilled, (state, action) => {
        const alert = action.payload;
        delete state.alerts[alert.id];
      });
  }
});

export default alertsSlice.reducer;
